import { useState } from 'react';
import type {
  AccountModel,
  BoPhan,
  ChiPhi,
  DichVu,
  DonViTinh,
  KheUoc,
  HopDong,
  Phi,
  Tax,
  VuViec
} from '@/types/schemas';

export interface ServiceFormFieldState {
  dvt: DonViTinh | null;
  tkDoanhThu: AccountModel | null;
  tkChiPhi: AccountModel | null;
  maThue: Tax | null;
  maChiPhi: ChiPhi | null;
  maPhi: Phi | null;
  maBoPhan: BoPhan | null;
  maHopDong: HopDong | null;
  kheUoc: KheUoc | null;
  maVuViec: VuViec | null;
}

export interface ServiceFormFieldActions {
  setDvt: (dvt: DonViTinh) => void;
  setTkDoanhThu: (tkDoanhThu: AccountModel) => void;
  setTkChiPhi: (tkChiPhi: AccountModel) => void;
  setMaThue: (maThue: Tax) => void;
  setMaChiPhi: (maChiPhi: ChiPhi) => void;
  setMaPhi: (maPhi: Phi) => void;
  setMaBoPhan: (maBoPhan: BoPhan) => void;
  setMaHopDong: (maHopDong: HopDong) => void;
  setKheUoc: (kheUoc: KheUoc) => void;
  setMaVuViec: (maVuViec: VuViec) => void;

  // Utility functions
  resetState: () => void;
  updateState: (updates: Partial<ServiceFormFieldState>) => void;
}

export interface UseServiceFormFieldStateReturn {
  state: ServiceFormFieldState;
  actions: ServiceFormFieldActions;
}

const initialState: ServiceFormFieldState = {
  dvt: null,
  tkDoanhThu: null,
  tkChiPhi: null,
  maThue: null,
  maChiPhi: null,
  maPhi: null,
  maBoPhan: null,
  maHopDong: null,
  kheUoc: null,
  maVuViec: null
};

function transformInitialData(initialData?: DichVu): Partial<ServiceFormFieldState> {
  if (!initialData) return {};

  return {
    dvt: initialData.dvt_data || null,
    tkDoanhThu: initialData.tk_dt_data || null,
    tkChiPhi: initialData.tk_cp_data || null,
    maThue: initialData.ma_thue_data || null,
    maChiPhi: initialData.ma_cp_data || null,
    maPhi: initialData.ma_phi_data || null,
    maBoPhan: initialData.ma_bp_data || null,
    maHopDong: (initialData.ma_hd_data as HopDong | null | undefined) || null,
    kheUoc: initialData.ma_ku_data || null,
    maVuViec: initialData.ma_vv_data || null
  };
}

export function useServiceFormFieldState(initialData?: DichVu): UseServiceFormFieldStateReturn {
  const [state, setState] = useState<ServiceFormFieldState>({
    ...initialState,
    ...transformInitialData(initialData)
  });

  const actions: ServiceFormFieldActions = {
    setDvt: (dvt: DonViTinh) => {
      setState(prev => ({ ...prev, dvt }));
    },

    setTkDoanhThu: (tkDoanhThu: AccountModel) => {
      setState(prev => ({ ...prev, tkDoanhThu }));
    },

    setTkChiPhi: (tkChiPhi: AccountModel) => {
      setState(prev => ({ ...prev, tkChiPhi }));
    },

    setMaThue: (maThue: Tax) => {
      setState(prev => ({ ...prev, maThue }));
    },

    setMaChiPhi: (maChiPhi: ChiPhi) => {
      setState(prev => ({ ...prev, maChiPhi }));
    },

    setMaPhi: (maPhi: Phi) => {
      setState(prev => ({ ...prev, maPhi }));
    },

    setMaBoPhan: (maBoPhan: BoPhan) => {
      setState(prev => ({ ...prev, maBoPhan }));
    },

    setMaHopDong: (maHopDong: HopDong) => {
      setState(prev => ({ ...prev, maHopDong }));
    },

    setKheUoc: (kheUoc: KheUoc) => {
      setState(prev => ({ ...prev, kheUoc }));
    },

    setMaVuViec: (maVuViec: VuViec) => {
      setState(prev => ({ ...prev, maVuViec }));
    },

    resetState: () => {
      setState(initialState);
    },

    updateState: (updates: Partial<ServiceFormFieldState>) => {
      setState(prev => ({ ...prev, ...updates }));
    }
  };

  return { state, actions };
}
